import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;

const getClosureMsgData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/closureMessages/${instanceId}?apiName=closureMsg`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `closureMsg`,
      message: `Something went wrong in getClosureMsgData API => ${error}`,
    });
    logMessage(
      `closureMsg`,
      `Something went wrong in getClosureMsgData API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getClosureMsgData api endpoint");
  }
};

export const getClosureMsg = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>("closureMsg/getClosureMsg", async (_args, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  return await getClosureMsgData(instanceId, rejectWithValue);
});

export interface MediaMgtData {
  instanceId: string;
  languageCode: string;
  language: string;
  fileName: string;
  type: string;
  [key: string]: unknown;
}

export const getClosureMsgMediaFile = createAsyncThunk<
  MediaMgtData & { mediaContent: unknown },
  MediaMgtData
>(
  "clsoureMsg/getClosureMsgMediaFile",
  async (mediaMgtData: MediaMgtData, { rejectWithValue }) => {
    const { instanceId, languageCode, language, fileName, type } = mediaMgtData;
    try {
      const response = await awsClientGateway({
        pathTemplate: `/mediaFile/${instanceId}/${languageCode}-${language}/${fileName}/${type}`,
        method: "GET",
      });
      const result = {
        ...mediaMgtData,
        mediaContent: response.data.mediaContent,
      };
      return result;
    } catch (error) {
      clientLog({
        component: `clsoureMsg`,
        message: `Something went wrong in getClosureMsgMediaFile API => ${error}`,
      });
      logMessage(
        `clsoureMsg`,
        `Something went wrong in getClosureMsgMediaFile API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in getClosureMsgMediaFile api endpoint");
    }
  },
);

export const createOrUpdateClosureMsg = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "closureMsg/createOrUpdateClosureMsg",
  async (closureMsgData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/closureMessages/${instanceId}`,
        method: "POST",
        body: { ...closureMsgData },
      });
      return await getClosureMsgData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `closureMsg`,
        message: `Something went wrong in createOrUpdateClosureMsg API => ${error}`,
      });
      logMessage(
        `closureMsg`,
        `Something went wrong in createOrUpdateClosureMsg API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in createOrUpdateClosureMsg api endpoint");
    }
  },
);

export const deleteClosureMsg = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "closureMsg/deleteClosureMsg",
  async (clsoureMsgData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/closureMessages/${instanceId}`,
        method: "DELETE",
        body: { ...clsoureMsgData },
      });
      return await getClosureMsgData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `closureMsg`,
        message: `Something went wrong in deleteClosureMsg API => ${error}`,
      });
      logMessage(
        `closureMsg`,
        `Something went wrong in deleteClosureMsg API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in deleteClosureMsg api endpoint");
    }
  },
);

export const updateRepeatOccureneceClosureMsg = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "closureMsg/updateRepeatOccureneceClosureMsg",
  async (clsoureMsgData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/closureMessages/${instanceId}`,
        method: "POST",
        body: clsoureMsgData,
      });
      return await getClosureMsgData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `closureMsg`,
        message: `Something went wrong in updateRepeatOccureneceClosureMsg API => ${error}`,
      });
      logMessage(
        `closureMsg`,
        `Something went wrong in updateRepeatOccureneceClosureMsg API => ${error}`,
        `error`,
      );
      return rejectWithValue(
        "Error in updateRepeatOccureneceClosureMsg api endpoint",
      );
    }
  },
);

export const getResetClosureMsg = createAction("closureMsg/getResetClosureMsg");
